import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache, Mutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LocaleLayout } from "@/components/LocaleLayout";
import { useMessageNotifications } from "@/hooks/useMessageNotifications";
import { useAppointmentNotifications } from "@/hooks/useAppointmentNotifications";
import Index from "./pages/Index";
import Annonces from "./pages/Annonces";
import PosterAnnonce from "./pages/PosterAnnonce";
import ProfilArtisan from "./pages/ProfilArtisan";
import RechercheArtisans from "./pages/RechercheArtisans";
import Inscription from "./pages/Inscription";
import InscriptionClient from "./pages/InscriptionClient";
import Connexion from "./pages/Connexion";
import Messagerie from "./pages/Messagerie";
import DashboardArtisan from "./pages/DashboardArtisan";
import AnnonceDetail from "./pages/AnnonceDetail";
import MesAnnonces from "./pages/MesAnnonces";
import MonProfil from "./pages/MonProfil";
import Favoris from "./pages/Favoris";
import FavorisArtisans from "./pages/FavorisArtisans";
import MonProfilArtisan from "./pages/MonProfilArtisan";
import EspaceArtisan from "./pages/EspaceArtisan";
import ArtisanLogin from "./pages/ArtisanLogin";
import APropos from "./pages/APropos";
import Aide from "./pages/Aide";
import Parametres from "./pages/Parametres";
import MesRdvClient from "./pages/MesRdvClient";
import MesRdvArtisan from "./pages/MesRdvArtisan";
import MesDevis from "./pages/MesDevis";
import ResetPassword from "./pages/ResetPassword";
import ModifierAnnonce from "./pages/ModifierAnnonce";
import CGU from "./pages/CGU";
import Confidentialite from "./pages/Confidentialite";
import MentionsLegales from "./pages/MentionsLegales";
import NotFound from "./pages/NotFound";
import AuthCallback from "./pages/AuthCallback";
import DevisPage from "./pages/devis/DevisPage";
import DevisPublicPage from "./pages/devis/DevisPublicPage";
import { AdminRoute } from "@/components/AdminRoute";
import { AdminLayout } from "@/admin/components/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminVerifications from "./pages/admin/AdminVerifications";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminAdsModeration from "./pages/admin/AdminAdsModeration";
import AdminReports from "./pages/admin/AdminReports";
import AdminAuditLogs from "./pages/admin/AdminAuditLogs";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminBanners from "./pages/admin/AdminBanners";

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error: unknown, _variables: unknown, _context: unknown, mutation: Mutation) => {
      Sentry.withScope((scope) => {
        scope.setTag("mutation_key", String(mutation.options.mutationKey ?? "unknown"));
        scope.setExtra("variables", _variables);
        Sentry.captureException(error);
      });
    },
  }),
});

const AppRoutes = () => {
  useMessageNotifications();
  useAppointmentNotifications();

  return (
    <Routes>
      {/* Redirect racine → /fr */}
      <Route path="/" element={<Navigate to="/fr" replace />} />

      {/* Routes admin — pas de préfixe de langue (interface admin en FR uniquement) */}
      <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/verifications" element={<AdminVerifications />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/annonces" element={<AdminAdsModeration />} />
        <Route path="/admin/signalements" element={<AdminReports />} />
        <Route path="/admin/audit" element={<AdminAuditLogs />} />
        <Route path="/admin/team" element={<AdminTeam />} />
        <Route path="/admin/banners" element={<AdminBanners />} />
      </Route>

      {/* Callback auth — pas de préfixe de langue */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Redirections devis sans locale → /fr/devis (évite le contenu dupliqué) */}
      <Route path="/devis" element={<Navigate to="/fr/devis" replace />} />
      <Route path="/devis/:quoteId" element={<Navigate to="/fr/devis" replace />} />

      {/* Lien public devis — accessible sans compte */}
      <Route path="/q/:token" element={<DevisPublicPage />} />

      {/* Routes avec préfixe de langue /fr/* et /ar/* */}
      <Route path="/:lang" element={<LocaleLayout />}>
        <Route index element={<Index />} />
        <Route path="annonces" element={<Annonces />} />
        <Route path="poster-annonce" element={<PosterAnnonce />} />
        {/* Static artisan sub-routes BEFORE dynamic /artisan/:id */}
        <Route path="artisan/login" element={<ArtisanLogin />} />
        <Route path="artisan/register" element={<Navigate to="inscription" replace />} />
        <Route path="artisan/:id" element={<ProfilArtisan />} />
        <Route path="recherche-artisans" element={<RechercheArtisans />} />
        <Route path="inscription" element={<Inscription />} />
        <Route path="inscription-client" element={<InscriptionClient />} />
        <Route path="connexion" element={<Connexion />} />
        <Route path="messagerie" element={<ProtectedRoute><Messagerie /></ProtectedRoute>} />
        <Route path="dashboard" element={<ProtectedRoute><DashboardArtisan /></ProtectedRoute>} />
        <Route path="annonce/:id" element={<AnnonceDetail />} />
        <Route path="mes-annonces" element={<ProtectedRoute><MesAnnonces /></ProtectedRoute>} />
        <Route path="modifier-annonce/:id" element={<ProtectedRoute><ModifierAnnonce /></ProtectedRoute>} />
        <Route path="mon-profil" element={<ProtectedRoute><MonProfil /></ProtectedRoute>} />
        <Route path="favoris" element={<ProtectedRoute><Favoris /></ProtectedRoute>} />
        <Route path="favoris-artisans" element={<ProtectedRoute><FavorisArtisans /></ProtectedRoute>} />
        <Route path="mon-profil-artisan" element={<ProtectedRoute><MonProfilArtisan /></ProtectedRoute>} />
        <Route path="artisan" element={<EspaceArtisan />} />
        <Route path="mes-rdv" element={<ProtectedRoute><MesRdvClient /></ProtectedRoute>} />
        <Route path="mes-rendez-vous" element={<ProtectedRoute><MesRdvArtisan /></ProtectedRoute>} />
        <Route path="mes-devis" element={<ProtectedRoute><MesDevis /></ProtectedRoute>} />
        <Route path="devis" element={<DevisPage />} />
        <Route path="a-propos" element={<APropos />} />
        <Route path="aide" element={<Aide />} />
        <Route path="cgu" element={<CGU />} />
        <Route path="confidentialite" element={<Confidentialite />} />
        <Route path="mentions-legales" element={<MentionsLegales />} />
        <Route path="parametres" element={<Parametres />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
